import React, { useEffect, useRef, useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const dialogoAbierto = () => !!document.querySelector('[role="dialog"], [role="alertdialog"]');

/** POS Android: ESC/atrás piden confirmación antes de cerrar y el teclado en pantalla del sistema no aparece
 *  (inputmode="none": el foco, el lector de códigos y un teclado físico siguen funcionando). */
export default function AndroidPosGuard({ enabled, keyboard = false, escOcupado = false }) {
  const [ask, setAsk] = useState(false);
  const saliendo = useRef(false);
  const ocupado = useRef(escOcupado);
  ocupado.current = escOcupado;

  useEffect(() => {
    if (!enabled) return;
    // Android convierte ESC (y el botón atrás) en "volver" del sistema. Chrome se salta las entradas de historial
    // creadas sin activación del usuario: solo cuentan pointerup/click/touchend y teclas distintas de ESC
    // (pointerdown táctil NO cuenta). Por eso la guarda se arma en esos eventos y se re-arma tras usarse.
    // Dos entradas de guarda: un "atrás" consume una y aún queda otra (ESC→pregunta→ESC no sale del POS).
    let prof = 0;
    let dlgAlPresionar = false;
    const armar = () => {
      if (saliendo.current) return;
      while (prof < 2) {
        prof += 1;
        window.history.pushState({ ...(window.history.state || {}), aiposGuard: Date.now() + prof }, "");
      }
    };
    const antes = (e) => {
      if (e.key === "Escape" || e.key === "Esc") { dlgAlPresionar = dialogoAbierto(); return; }
      armar();
    };
    if (navigator.userActivation?.hasBeenActive) armar();
    const onPop = () => {
      if (saliendo.current) return;
      prof = Math.max(0, prof - 1);
      setAsk(true);
    };
    // Fase burbuja: los diálogos (Radix) y los atajos del POS ya procesaron ESC; aquí solo evitamos que llegue al sistema.
    const onKey = (e) => {
      const k = e.key;
      if (k === "F5" || ((e.ctrlKey || e.metaKey) && (k === "r" || k === "R"))) { e.preventDefault(); return; }
      if (k !== "Escape" && k !== "Esc") return;
      const atendida = e.defaultPrevented || dlgAlPresionar;  // un diálogo ya la usó para cerrarse
      e.preventDefault();
      if (!atendida && !dialogoAbierto() && !ocupado.current) setAsk(true);
    };
    const onUnload = (e) => { if (saliendo.current) return; e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("pointerup", armar, true);
    window.addEventListener("click", armar, true);
    window.addEventListener("touchend", armar, true);
    window.addEventListener("keydown", antes, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("popstate", onPop);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("pointerup", armar, true);
      window.removeEventListener("click", armar, true);
      window.removeEventListener("touchend", armar, true);
      window.removeEventListener("keydown", antes, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const SEL = "input:not([type=hidden]):not([type=checkbox]):not([type=radio]), textarea";
    const uno = (el) => {
      if (keyboard) { if (el.dataset.aiposKb) { el.setAttribute("inputmode", el.dataset.aiposKb === "1" ? "" : el.dataset.aiposKb); delete el.dataset.aiposKb; } }
      else if (!el.dataset.aiposKb) { el.dataset.aiposKb = el.getAttribute("inputmode") || "1"; el.setAttribute("inputmode", "none"); }
    };
    const marcar = (root) => { if (root.matches?.(SEL)) uno(root); root.querySelectorAll?.(SEL).forEach(uno); };
    marcar(document);
    const obs = new MutationObserver((muts) => muts.forEach((m) => m.addedNodes.forEach((n) => n.nodeType === 1 && marcar(n))));
    obs.observe(document.body, { childList: true, subtree: true });
    return () => { obs.disconnect(); };
  }, [enabled, keyboard]);

  const cerrar = () => {
    saliendo.current = true;
    setAsk(false);
    window.close();
    // Si el navegador no deja cerrar la pestaña: volvemos al inicio del historial y, si seguimos aquí, se deja la página en blanco
    setTimeout(() => {
      if (window.history.length > 1) window.history.go(-(window.history.length - 1));
      setTimeout(() => { if (!document.hidden) window.location.replace("about:blank"); }, 700);
    }, 150);
  };

  if (!enabled) return null;
  return (
    <AlertDialog open={ask} onOpenChange={setAsk}>
      <AlertDialogContent data-testid="android-close-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>¿Desea cerrar Ai-POS?</AlertDialogTitle>
          <AlertDialogDescription>Si cierras la aplicación, la caja dejará de estar disponible hasta que la vuelvas a abrir.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="android-close-no">No</AlertDialogCancel>
          <AlertDialogAction data-testid="android-close-yes" onClick={cerrar} className="bg-rose-600 hover:bg-rose-700">Sí, cerrar</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
